import { n as MICROSECONDS_PER_SECOND } from "./timestamp-CRYLj3P9.js";
//#region src/time/clock-model.ts
const UNCONVERGED = {
	samples: 0,
	residualStdUs: NaN,
	rateRatio: NaN,
	converged: false,
	spanSeconds: 0
};
//#endregion
//#region src/time/windowed-clock.ts
/**
* Least-squares fit over a sliding window.
*
* The default model, on the strength of published validation: in a dual-device
* photodiode experiment this approach held event-onset jitter to about 5 ms on
* one headband and 42 ms on another, where offset-only and adaptive models
* reached 130–180 ms on the same data.
*
* The window is what makes it work. A fit over the whole session cannot follow
* slow drift; a fit over the last few packets is dominated by burst structure.
* Thirty seconds is long enough to average out bursts and short enough to track
* drift, and refitting once a second keeps the cost negligible.
*/
var WindowedRegressionClock = class {
	name = "windowed";
	#windowUs;
	#refitUs;
	#minSamples;
	#minSpanUs;
	#observations = [];
	#slope = NaN;
	#intercept = NaN;
	#quality = UNCONVERGED;
	#lastFitHost = null;
	#dirty = false;
	constructor(options = {}) {
		this.#windowUs = (options.windowSeconds ?? 30) * MICROSECONDS_PER_SECOND;
		this.#refitUs = (options.refitIntervalSeconds ?? 1) * MICROSECONDS_PER_SECOND;
		this.#minSamples = options.minSamples ?? 16;
		this.#minSpanUs = (options.minSpanSeconds ?? 1) * MICROSECONDS_PER_SECOND;
	}
	get quality() {
		return this.#quality;
	}
	observe(deviceSeconds, hostTime) {
		this.#observations.push({
			device: deviceSeconds,
			host: hostTime
		});
		this.#dirty = true;
		const cutoff = hostTime - this.#windowUs;
		let drop = 0;
		while (drop < this.#observations.length && this.#observations[drop].host < cutoff) drop++;
		if (drop > 0) this.#observations = this.#observations.slice(drop);
		if (this.#lastFitHost === null || hostTime - this.#lastFitHost >= this.#refitUs) {
			this.#fit();
			this.#lastFitHost = hostTime;
		}
	}
	map(deviceSeconds) {
		if (this.#dirty && this.#quality.converged === false) this.#fit();
		if (!Number.isFinite(this.#slope)) {
			const last = this.#observations.at(-1);
			if (last === void 0) return NaN;
			return last.host + (deviceSeconds - last.device) * MICROSECONDS_PER_SECOND;
		}
		return this.#intercept + this.#slope * deviceSeconds;
	}
	reset() {
		this.#observations = [];
		this.#slope = NaN;
		this.#intercept = NaN;
		this.#quality = UNCONVERGED;
		this.#lastFitHost = null;
		this.#dirty = false;
	}
	#discardFit() {
		this.#slope = NaN;
		this.#intercept = NaN;
	}
	#fit() {
		this.#dirty = false;
		const n = this.#observations.length;
		if (n < 2) {
			this.#discardFit();
			this.#quality = {
				...UNCONVERGED,
				samples: n
			};
			return;
		}
		let sumDevice = 0;
		let sumHost = 0;
		for (const o of this.#observations) {
			sumDevice += o.device;
			sumHost += o.host;
		}
		const meanDevice = sumDevice / n;
		const meanHost = sumHost / n;
		let covariance = 0;
		let variance = 0;
		let hostVariance = 0;
		for (const o of this.#observations) {
			const d = o.device - meanDevice;
			covariance += d * (o.host - meanHost);
			variance += d * d;
			hostVariance += (o.host - meanHost) ** 2;
		}
		const spanUs = this.#observations[n - 1].host - this.#observations[0].host;
		if (variance === 0 || hostVariance === 0) {
			this.#discardFit();
			this.#quality = {
				samples: n,
				residualStdUs: NaN,
				rateRatio: NaN,
				converged: false,
				spanSeconds: spanUs / MICROSECONDS_PER_SECOND
			};
			return;
		}
		this.#slope = covariance / variance;
		this.#intercept = meanHost - this.#slope * meanDevice;
		let sumSquared = 0;
		for (const o of this.#observations) {
			const residual = o.host - (this.#intercept + this.#slope * o.device);
			sumSquared += residual * residual;
		}
		this.#quality = {
			samples: n,
			residualStdUs: Math.sqrt(sumSquared / n),
			rateRatio: this.#slope / MICROSECONDS_PER_SECOND,
			converged: n >= this.#minSamples && spanUs >= this.#minSpanUs,
			spanSeconds: spanUs / MICROSECONDS_PER_SECOND
		};
	}
};
function windowedClock(options = {}) {
	return new WindowedRegressionClock(options);
}
//#endregion
//#region src/time/robust-clock.ts
/**
* Offset-only model that tracks the minimum-latency envelope.
*
* Assumes the device and host clocks run at the same rate and estimates only the
* offset between them, from a low quantile of `hostTime − deviceSeconds`.
*
* Useful when the nominal sample rate is trusted and drift is not a concern —
* short recordings, or a sanity check against the regression. Published
* comparison puts it behind windowed regression on longer sessions, which is why
* it is not the default: assuming the rate is exactly right is precisely the
* assumption that fails over minutes.
*/
var RobustOffsetClock = class {
	name = "robust";
	#windowUs;
	#quantile;
	#minSamples;
	#minSpanUs;
	#observations = [];
	#offset = NaN;
	#quality = UNCONVERGED;
	constructor(options = {}) {
		this.#windowUs = (options.windowSeconds ?? 30) * MICROSECONDS_PER_SECOND;
		this.#quantile = options.quantile ?? .1;
		this.#minSamples = options.minSamples ?? 16;
		this.#minSpanUs = (options.minSpanSeconds ?? 1) * MICROSECONDS_PER_SECOND;
	}
	get quality() {
		return this.#quality;
	}
	observe(deviceSeconds, hostTime) {
		this.#observations.push({
			device: deviceSeconds,
			host: hostTime,
			offset: hostTime - deviceSeconds * MICROSECONDS_PER_SECOND
		});
		const cutoff = hostTime - this.#windowUs;
		let drop = 0;
		while (drop < this.#observations.length && this.#observations[drop].host < cutoff) drop++;
		if (drop > 0) this.#observations = this.#observations.slice(drop);
		this.#update();
	}
	map(deviceSeconds) {
		if (!Number.isFinite(this.#offset)) return NaN;
		return this.#offset + deviceSeconds * MICROSECONDS_PER_SECOND;
	}
	reset() {
		this.#observations = [];
		this.#offset = NaN;
		this.#quality = UNCONVERGED;
	}
	#update() {
		const n = this.#observations.length;
		if (n === 0) {
			this.#quality = UNCONVERGED;
			return;
		}
		const offsets = this.#observations.map((o) => o.offset).toSorted((a, b) => a - b);
		this.#offset = offsets[Math.min(offsets.length - 1, Math.floor(this.#quantile * offsets.length))];
		let sumSquared = 0;
		for (const o of this.#observations) {
			const residual = o.offset - this.#offset;
			sumSquared += residual * residual;
		}
		const spanUs = this.#observations[n - 1].host - this.#observations[0].host;
		this.#quality = {
			samples: n,
			residualStdUs: Math.sqrt(sumSquared / n),
			rateRatio: 1,
			converged: n >= this.#minSamples && spanUs >= this.#minSpanUs,
			spanSeconds: spanUs / MICROSECONDS_PER_SECOND
		};
	}
};
function robustClock(options = {}) {
	return new RobustOffsetClock(options);
}
/**
* Passthrough: host time is taken as-is from the first observation onward.
*
* Applies no correction at all, so every sample inherits the burst structure of
* BLE delivery. Exists as a baseline for comparison and for callers who want to
* do their own timing — not because it is a reasonable default.
*/
var RawArrivalClock = class {
	name = "raw";
	#anchorDevice = null;
	#anchorHost = null;
	#count = 0;
	#lastHost = 0;
	get quality() {
		return {
			samples: this.#count,
			residualStdUs: 0,
			rateRatio: 1,
			converged: this.#count > 0,
			spanSeconds: this.#anchorHost === null ? 0 : (this.#lastHost - this.#anchorHost) / MICROSECONDS_PER_SECOND
		};
	}
	observe(deviceSeconds, hostTime) {
		this.#anchorDevice ??= deviceSeconds;
		this.#anchorHost ??= hostTime;
		this.#lastHost = hostTime;
		this.#count++;
	}
	map(deviceSeconds) {
		if (this.#anchorHost === null || this.#anchorDevice === null) return NaN;
		return this.#anchorHost + (deviceSeconds - this.#anchorDevice) * MICROSECONDS_PER_SECOND;
	}
	reset() {
		this.#anchorDevice = null;
		this.#anchorHost = null;
		this.#count = 0;
	}
};
function rawClock() {
	return new RawArrivalClock();
}
//#endregion
export { WindowedRegressionClock as a, robustClock as i, RobustOffsetClock as n, windowedClock as o, rawClock as r, UNCONVERGED as s, RawArrivalClock as t };

//# sourceMappingURL=time-B-JTEZC3.js.map